import type { Box, InternalNode, Node, NodeDimensionChange, NodePositionChange } from '@xyflow/react';

function nodeToBox(node: InternalNode): Box {
  const { x, y } = node.internals.positionAbsolute;
  const w = node.measured?.width ?? 0;
  const h = node.measured?.height ?? 0;
  return { x, y, x2: x + w, y2: y + h };
}

function rfNodeToBox(node: Node): Box {
  const x = node.position.x;
  const y = node.position.y;
  const w = node.measured?.width ?? 0;
  const h = node.measured?.height ?? 0;
  return { x, y, x2: x + w, y2: y + h };
}

// ── Types ─────────────────────────────────────────────────────────────────────

export type Orientation = 'horizontal' | 'vertical';

export type HelperLine = {
  node: Node;
  nodeBox: Box;
  orientation: Orientation;
  position: number;
  anchorName: string;
};

type AnchorResolver = (node: Node, box: Box) => number;

type Anchor = {
  orientation: Orientation;
  resolve: AnchorResolver;
};

export type AnchorMatch = {
  anchorName: string;
  sourcePosition: number;
  anchor: Anchor;
  line: HelperLine;
};

type CandidateLine = {
  line: HelperLine;
  lineDist: number;
  nodeDist: number;
};

// ── Config ────────────────────────────────────────────────────────────────────

export const SNAP_RADIUS = 8;

export const ANCHORS: Record<string, Anchor> = {
  top:     { orientation: 'horizontal', resolve: (_, box) => box.y },
  bottom:  { orientation: 'horizontal', resolve: (_, box) => box.y2 },
  centerY: { orientation: 'horizontal', resolve: (_, box) => (box.y + box.y2) / 2 },
  left:    { orientation: 'vertical',   resolve: (_, box) => box.x },
  right:   { orientation: 'vertical',   resolve: (_, box) => box.x2 },
  centerX: { orientation: 'vertical',   resolve: (_, box) => (box.x + box.x2) / 2 },
};

// ── Spatial index ──────────────────────────────────────────────────────────────

function overlapArea(a: Box, b: Box): number {
  const xOverlap = Math.max(0, Math.min(a.x2, b.x2) - Math.max(a.x, b.x));
  const yOverlap = Math.max(0, Math.min(a.y2, b.y2) - Math.max(a.y, b.y));
  return xOverlap * yOverlap;
}

function boxDistance(a: Box, b: Box): number {
  const dx = Math.max(0, Math.max(a.x - b.x2, b.x - a.x2));
  const dy = Math.max(0, Math.max(a.y - b.y2, b.y - a.y2));
  return Math.sqrt(dx * dx + dy * dy);
}

function isInViewport(a: Box, b: Box): boolean {
  return a.x < b.x2 && a.x2 > b.x && a.y < b.y2 && a.y2 > b.y;
}

export class SpatialIndex {
  private xLines: HelperLine[] = [];
  private yLines: HelperLine[] = [];
  private lastH: HelperLine | undefined;
  private lastV: HelperLine | undefined;

  initialize(lines: HelperLine[]): void {
    this.xLines = lines.filter(l => l.orientation === 'vertical').sort((a, b) => a.position - b.position);
    this.yLines = lines.filter(l => l.orientation === 'horizontal').sort((a, b) => a.position - b.position);
    this.lastH = undefined;
    this.lastV = undefined;
  }

  search(orientation: Orientation, pos: number, node: Node, viewportBox: Box): HelperLine | undefined {
    const lines = orientation === 'horizontal' ? this.yLines : this.xLines;
    const [vMin, vMax] = orientation === 'horizontal'
      ? [viewportBox.y, viewportBox.y2]
      : [viewportBox.x, viewportBox.x2];

    const candidates: CandidateLine[] = [];
    for (const line of lines) {
      if (line.position < vMin) continue;
      if (line.position > vMax) break;
      if (line.node.id === node.id || line.node.parentId === node.id) continue;
      if (!isInViewport(line.nodeBox, viewportBox)) continue;
      const lineDist = Math.abs(line.position - pos);
      if (lineDist > SNAP_RADIUS) continue;
      const overlap = overlapArea(line.nodeBox, rfNodeToBox(node));
      candidates.push({ line, lineDist, nodeDist: overlap > 0 ? 0 : boxDistance(line.nodeBox, rfNodeToBox(node)) });
    }

    const lastLine = orientation === 'horizontal' ? this.lastH : this.lastV;
    candidates.sort((a, b) => {
      if (lastLine) {
        if (a.line === lastLine && b.line !== lastLine) return -1;
        if (b.line === lastLine && a.line !== lastLine) return 1;
      }
      if (Math.abs(a.lineDist - b.lineDist) <= 0.5) return a.nodeDist - b.nodeDist;
      return a.lineDist - b.lineDist;
    });

    const best = candidates[0]?.line;
    if (best) {
      if (orientation === 'horizontal') this.lastH = best;
      else this.lastV = best;
    }
    return best;
  }
}

// ── Build helpers ──────────────────────────────────────────────────────────────

export function buildHelperLines(
  nodes: Node[],
  getInternalNode: (id: string) => InternalNode | undefined,
): HelperLine[] {
  const lines: HelperLine[] = [];
  for (const node of nodes) {
    const internal = getInternalNode(node.id);
    if (!internal) continue;
    const nodeBox = nodeToBox(internal);
    for (const [anchorName, anchor] of Object.entries(ANCHORS)) {
      lines.push({ nodeBox, node, orientation: anchor.orientation, position: anchor.resolve(node, nodeBox), anchorName });
    }
  }
  return lines;
}

// ── Query helpers ──────────────────────────────────────────────────────────────

export function getHelperLines(
  index: SpatialIndex,
  viewportBox: Box,
  node: Node,
  nodeBox: Box,
  validAnchors: string[] = Object.keys(ANCHORS),
): { horizontal: AnchorMatch | undefined; vertical: AnchorMatch | undefined } {
  const result: { horizontal: AnchorMatch | undefined; vertical: AnchorMatch | undefined } = {
    horizontal: undefined,
    vertical: undefined,
  };

  for (const anchorName of validAnchors) {
    const anchor = ANCHORS[anchorName];
    if (!anchor) continue;
    const pos = anchor.resolve(node, nodeBox);
    const line = index.search(anchor.orientation, pos, node, viewportBox);
    if (!line) continue;
    const dist = Math.abs(pos - line.position);
    const current = result[anchor.orientation];
    if (!current || dist < Math.abs(current.sourcePosition - current.line.position)) {
      result[anchor.orientation] = { anchorName, sourcePosition: pos, anchor, line };
    }
  }

  return result;
}

// ── Snap application ───────────────────────────────────────────────────────────

export function snapToHelperLines(
  node: Node,
  internalNode: InternalNode,
  snapChange: NodePositionChange,
  positionChange?: NodePositionChange,
  dimensionChange?: NodeDimensionChange,
  parentNode?: InternalNode,
  hMatch?: AnchorMatch,
  vMatch?: AnchorMatch,
): { snappedX: boolean; snappedY: boolean } {
  if (!snapChange.position) return { snappedX: false, snappedY: false };

  let snappedX = false;
  let snappedY = false;
  const bounds = {
    x: snapChange.position.x, y: snapChange.position.y,
    x2: snapChange.position.x + (node.measured?.width ?? 0),
    y2: snapChange.position.y + (node.measured?.height ?? 0),
  };

  if (hMatch) {
    const deltaY = hMatch.anchor.resolve(node, bounds) - hMatch.line.position;
    if (Math.abs(deltaY) <= SNAP_RADIUS) {
      snapChange.position.y -= deltaY;
      if (dimensionChange?.dimensions) {
        if (positionChange) {
          const bottom = internalNode.internals.positionAbsolute.y + (node.measured?.height ?? 0);
          dimensionChange.dimensions.height = bottom - hMatch.line.position;
        } else {
          dimensionChange.dimensions.height = hMatch.line.position - internalNode.internals.positionAbsolute.y;
        }
      }
      snappedY = true;
    }
  }

  if (vMatch) {
    const deltaX = vMatch.anchor.resolve(node, bounds) - vMatch.line.position;
    if (Math.abs(deltaX) <= SNAP_RADIUS) {
      snapChange.position.x -= deltaX;
      if (dimensionChange?.dimensions) {
        if (positionChange) {
          const right = internalNode.internals.positionAbsolute.x + (node.measured?.width ?? 0);
          dimensionChange.dimensions.width = right - vMatch.line.position;
        } else {
          dimensionChange.dimensions.width = vMatch.line.position - internalNode.internals.positionAbsolute.x;
        }
      }
      snappedX = true;
    }
  }

  if (parentNode && (snappedX || snappedY)) {
    snapChange.position.x -= parentNode.position.x;
    snapChange.position.y -= parentNode.position.y;
  }

  return { snappedX, snappedY };
}
